import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Award, Loader2, CheckCircle, Sparkles, ArrowRight, Clock, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { calculateScore, SEARCH_CONFIG, getPrefixVariant, extractDegreePrefix } from '../utils/rncpSearchUtils';

const Onboarding2 = () => {
  const navigate = useNavigate();

  const [siret, setSiret] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('siret'); // siret, processing, results
  const [organism, setOrganism] = useState(null);
  const [formations, setFormations] = useState([]);
  const [enrichedFormations, setEnrichedFormations] = useState([]);
  const [fusionResults, setFusionResults] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [showOnlyFused, setShowOnlyFused] = useState(true);
  const [progressMessage, setProgressMessage] = useState('');

  // Fonction de scoring client (même fonction que TestFusionOnboarding)
  const calculateClientScore = (query, title, rncpCode = '') => {
    if (!query || !title) {
      console.warn('calculateClientScore: query ou title manquant', { query, title });
      return 0;
    }
    return calculateScore(query, title, rncpCode, true);
  };

  // Formatage SIRET
  const formatSiret = (value) => {
    const numbers = value.replace(/\s/g, '');
    if (numbers.length <= 14) {
      return numbers.replace(/(\d{3})(\d{3})?(\d{3})?(\d{2})?(\d{3})?/, (match, p1, p2, p3, p4, p5) => {
        let result = p1;
        if (p2) result += ' ' + p2;
        if (p3) result += ' ' + p3;
        if (p4) result += ' ' + p4;
        if (p5) result += ' ' + p5;
        return result;
      });
    }
    return value;
  };

  // Rechercher l'organisme et lancer le processus automatique (EXACT comme TestFusionOnboarding)
  const handleSearchOrganism = async () => {
    const cleanSiret = siret.replace(/\s/g, '');

    if (!cleanSiret || cleanSiret.length !== 14 || !/^\d{14}$/.test(cleanSiret)) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    setLoading(true);
    setStep('processing');
    setProgressMessage('🔍 Recherche de l\'organisme...');

    try {
      // Étape 1: Scraper Intercariforef (EXACT comme TestFusionOnboarding)
      const scrapeResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/scrape`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ siret: cleanSiret })
        }
      );

      const scrapeData = await scrapeResponse.json();

      if (!scrapeData.success || !scrapeData.data) {
        toast.error('Organisme non trouvé');
        setStep('siret');
        setLoading(false);
        return;
      }

      setOrganism({
        name: scrapeData.data.organism?.name || 'Organisme trouvé',
        siret: cleanSiret,
        totalFormations: scrapeData.data.totalFormations
      });

      // Étape 2: Récupérer les formations (EXACT comme TestFusionOnboarding)
      setProgressMessage('📚 Récupération des formations...');

      const formationsResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/formations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            siret: cleanSiret,
            scrapeSource: 'intercariforef'
          })
        }
      );

      const formationsData = await formationsResponse.json();

      if (!formationsData.success || !formationsData.formations) {
        toast.error('Aucune formation trouvée');
        setStep('siret');
        setLoading(false);
        return;
      }

      // Dédupliquer les formations
      const seen = new Set();
      const uniqueFormations = formationsData.formations.filter(f => {
        const key = f.title.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setFormations(uniqueFormations);
      toast.success(`${uniqueFormations.length} formations trouvées`);

      // Lancer le processus automatique
      await processAutomatically(uniqueFormations);

    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la recherche');
      setStep('siret');
      setLoading(false);
    }
  };

  // Processus automatique complet
  const processAutomatically = async (formationsData) => {
    try {
      // Étape 2: Enrichissement automatique
      setProgressMessage('📚 Enrichissement des formations en cours...');

      const enriched = [];
      let totalFormations = formationsData.length;
      let processedCount = 0;

      for (const formation of formationsData) {
        processedCount++;
        setProgressMessage(`📚 Enrichissement ${processedCount}/${totalFormations} : ${formation.title}`);

        let rncpCode = formation.rncp || '';
        let score = 0;

        // Si pas de RNCP, chercher via Kit Apprentissage (MULTI-RECHERCHE exacte comme TestFusionOnboarding)
        if (!rncpCode) {
          try {
            const headers = {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            };
            const baseUrl = `${import.meta.env.VITE_API_URL}/kit-apprentissage/search/intitule`;

            // Extraire le préfixe
            const { prefix, coreQuery } = extractDegreePrefix(formation.title);
            const apiSearchTerm = formation.title;

            // Préparer les recherches multiples (EXACTEMENT comme TestFusionOnboarding)
            const searchPromises = [];

            // TYPE 1: Recherche complète
            searchPromises.push(
              fetch(`${baseUrl}?q=${encodeURIComponent(apiSearchTerm)}&limit=100`, { headers })
                .then(r => r.json())
                .catch(() => ({ success: false, data: [] }))
            );

            // TYPE 2: Sans préfixe
            if (prefix && coreQuery && coreQuery !== apiSearchTerm) {
              searchPromises.push(
                fetch(`${baseUrl}?q=${encodeURIComponent(coreQuery)}&limit=100`, { headers })
                  .then(r => r.json())
                  .catch(() => ({ success: false, data: [] }))
              );
            }

            // TYPE 5: 2 premiers mots spécifiques
            const { stopWords, genericWords } = SEARCH_CONFIG;
            const queryForKeywords = (prefix ? coreQuery : apiSearchTerm)
              .replace(/-/g, ' ')
              .replace(/[,;:.!?()]/g, ' ')
              .replace(/\s+/g, ' ')
              .trim();

            const words = queryForKeywords.split(' ')
              .filter(w => w.length > 3 && !stopWords.includes(w.toLowerCase()))
              .map((w) => ({
                word: w,
                isGeneric: genericWords.includes(w.toLowerCase())
              }));

            const specificWords = words.filter(w => !w.isGeneric);

            if (specificWords.length >= 2) {
              const keyWords2 = specificWords.slice(0, 2).map(w => w.word).join(' ');
              if (keyWords2 && keyWords2 !== apiSearchTerm && keyWords2 !== coreQuery) {
                searchPromises.push(
                  fetch(`${baseUrl}?q=${encodeURIComponent(keyWords2)}&limit=100`, { headers })
                    .then(r => r.json())
                    .catch(() => ({ success: false, data: [] }))
                );
              }
            }

            // TYPE 6: Préfixe + 1er mot
            if (prefix && specificWords.length >= 1) {
              const queryWithPrefix = `${prefix} ${specificWords[0].word}`;
              searchPromises.push(
                fetch(`${baseUrl}?q=${encodeURIComponent(queryWithPrefix)}&limit=100`, { headers })
                  .then(r => r.json())
                  .catch(() => ({ success: false, data: [] }))
              );
            }

            // TYPE 7: Variante de préfixe
            if (prefix) {
              const prefixVariant = getPrefixVariant(prefix);
              if (prefixVariant && prefixVariant !== prefix.toUpperCase()) {
                const queryWithVariant = `${prefixVariant} ${coreQuery}`;
                searchPromises.push(
                  fetch(`${baseUrl}?q=${encodeURIComponent(queryWithVariant)}&limit=100`, { headers })
                    .then(r => r.json())
                    .catch(() => ({ success: false, data: [] }))
                );
              }
            }

            // Exécuter toutes les recherches en parallèle
            const responses = await Promise.all(searchPromises);

            // Combiner et dédupliquer par RNCP
            let allResults = [];
            const seenRncps = new Set();

            responses.forEach(resp => {
              if (resp.success && resp.data) {
                resp.data.forEach(result => {
                  if (!seenRncps.has(result.code_rncp)) {
                    seenRncps.add(result.code_rncp);
                    allResults.push(result);
                  }
                });
              }
            });

            // Scorer tous les résultats
            const scoredResults = allResults.map(result => ({
              ...result,
              score: calculateClientScore(formation.title, result.title, result.code_rncp)
            }));

            // Trier par score décroissant
            scoredResults.sort((a, b) => b.score - a.score);

            // Prendre le meilleur résultat
            if (scoredResults.length > 0 && scoredResults[0].score >= 50) {
              rncpCode = scoredResults[0].code_rncp;
              score = scoredResults[0].score;
            }
          } catch (err) {
            console.error('Erreur recherche RNCP:', err);
          }
        } else {
          score = 100;
        }

        if (!rncpCode) {
          console.log(`⚠️ Pas de RNCP trouvé pour: ${formation.title}`);
          continue;
        }

        // Récupérer les blocs et compétences (EXACTEMENT comme TestFusionOnboarding)
        try {
          const numeroRncp = rncpCode.replace(/^RNCP/i, '');
          const scrapingResponse = await fetch(
            `${import.meta.env.VITE_API_URL}/rncp-scraping/scrape`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ numeroRncp })
            }
          );

          const scrapingData = await scrapingResponse.json();

          if (scrapingData.success && scrapingData.data) {
            let competences = [];
            let blocks = [];

            if (scrapingData.data.blocsCompetences && Array.isArray(scrapingData.data.blocsCompetences)) {
              blocks = scrapingData.data.blocsCompetences.map(bloc => ({
                code: bloc.code,
                libelle: bloc.libelle,
                competences: bloc.competences || []
              }));

              scrapingData.data.blocsCompetences.forEach(bloc => {
                if (bloc.competences && Array.isArray(bloc.competences)) {
                  competences.push(...bloc.competences);
                }
              });
            }

            enriched.push({
              ...formation,
              rncpCode: `RNCP${numeroRncp}`,
              title: formation.title,
              score,
              competences,
              blocks,
              enriched: true
            });
          }
        } catch (err) {
          console.error('Erreur scraping:', err);
        }
      }

      setEnrichedFormations(enriched);

      if (enriched.length < 2) {
        toast.error('Au moins 2 formations enrichies sont nécessaires pour l\'analyse');
        setStep('siret');
        setLoading(false);
        return;
      }

      // Étape 3: Analyse de fusion
      setProgressMessage('🔗 Analyse des fusions en cours...');
      await analyzeFusions(enriched);

    } catch (error) {
      console.error('Erreur processus automatique:', error);
      toast.error('Erreur lors du traitement automatique');
      setStep('siret');
      setLoading(false);
    }
  };

  // Analyse de fusion (algorithme de Jaccard)
  const analyzeFusions = async (enrichedData) => {
    try {
      const threshold = 66; // Seuil de similarité fixe à 66%

      const jaccard = (str1, str2) => {
        const set1 = new Set(str1.toLowerCase().split(/\s+/));
        const set2 = new Set(str2.toLowerCase().split(/\s+/));
        const intersection = new Set([...set1].filter(x => set2.has(x)));
        const union = new Set([...set1, ...set2]);
        return (intersection.size / union.size) * 100;
      };

      const competencesList = [];
      enrichedData.forEach(formation => {
        formation.competences.forEach(comp => {
          competencesList.push({
            text: comp,
            certification_id: formation.rncpCode,
            certificationTitle: formation.title
          });
        });
      });

      const fused = [];
      const processed = new Set();

      competencesList.forEach((comp1, i) => {
        if (processed.has(i)) return;

        const similar = [comp1];
        const similarIndices = [i];

        competencesList.forEach((comp2, j) => {
          if (i === j || processed.has(j)) return;
          if (comp1.certification_id === comp2.certification_id) return;

          const similarity = jaccard(comp1.text, comp2.text);
          if (similarity >= threshold) {
            similar.push(comp2);
            similarIndices.push(j);
          }
        });

        similarIndices.forEach(idx => processed.add(idx));

        if (similar.length > 1) {
          const avgSimilarity = Math.round(
            similar.reduce((sum, s, idx) => {
              if (idx === 0) return sum;
              return sum + jaccard(comp1.text, s.text);
            }, 0) / (similar.length - 1)
          );

          fused.push({
            description: comp1.text,
            count: similar.length,
            isMerged: true,
            avgSimilarity,
            sources: similar.map(s => ({
              text: s.text,
              rncp: s.certification_id,
              formation: s.certificationTitle
            }))
          });
        }
      });

      const sorted = fused
        .filter(f => f.isMerged)
        .sort((a, b) => b.count - a.count);

      setFusionResults(sorted);

      // Étape 4: Préparer la comparaison
      if (sorted.length > 0 && enrichedData.length >= 2) {
        setProgressMessage('📊 Préparation de la comparaison...');
        await prepareComparison(enrichedData, sorted);
      } else {
        setLoading(false);
        setStep('results');
        toast.success('Analyse terminée !');
      }

    } catch (error) {
      console.error('Erreur analyse:', error);
      toast.error('Erreur lors de l\'analyse de fusion');
      setLoading(false);
      setStep('siret');
    }
  };

  // Préparer la comparaison des 2 formations avec le plus de similitudes
  const prepareComparison = (enrichedData, fusionData) => {
    const formationPairs = [];

    for (let i = 0; i < enrichedData.length; i++) {
      for (let j = i + 1; j < enrichedData.length; j++) {
        const formation1 = enrichedData[i];
        const formation2 = enrichedData[j];

        const sharedFusions = fusionData.filter(fusion => {
          const rncps = fusion.sources.map(s => s.rncp);
          return rncps.includes(formation1.rncpCode) && rncps.includes(formation2.rncpCode);
        });

        if (sharedFusions.length > 0) {
          formationPairs.push({
            formation1,
            formation2,
            sharedFusionCount: sharedFusions.length,
            sharedFusions
          });
        }
      }
    }

    if (formationPairs.length === 0) {
      setLoading(false);
      setStep('results');
      toast.success('Analyse terminée !');
      return;
    }

    formationPairs.sort((a, b) => b.sharedFusionCount - a.sharedFusionCount);
    const topPair = formationPairs[0];

    const totalCompetences1 = topPair.formation1.competences.length;
    const totalCompetences2 = topPair.formation2.competences.length;
    const fusedCount = topPair.sharedFusionCount;
    const unificationRate = Math.round((fusedCount / Math.min(totalCompetences1, totalCompetences2)) * 100);
    const timeSaved = fusedCount * 4;

    setComparisonData({
      formation1: topPair.formation1,
      formation2: topPair.formation2,
      sharedFusions: topPair.sharedFusions,
      fusedCount,
      unificationRate,
      timeSaved,
      totalFormations: enrichedData.length
    });

    setLoading(false);
    setStep('results');
    toast.success('✨ Analyse complète terminée !');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            ✨ Découvrez le potentiel de fusion de vos formations
          </h1>
          <p className="text-gray-600 text-lg">
            Entrez votre SIRET et laissez-nous analyser automatiquement vos opportunités d'optimisation
          </p>
        </div>

        {/* Étape 1: SIRET */}
        {step === 'siret' && (
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Votre organisme de formation</h2>
              <p className="text-gray-600">
                Commencez par entrer votre numéro SIRET
              </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSearchOrganism(); }}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro SIRET
                </label>
                <input
                  type="text"
                  value={siret}
                  onChange={(e) => setSiret(formatSiret(e.target.value))}
                  placeholder="123 456 789 01234"
                  maxLength={17}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg tracking-wider"
                />
                <p className="text-xs text-gray-500 mt-2 text-center">
                  14 chiffres (espaces automatiques)
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Recherche...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Analyser mes formations
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Ce qui va se passer :
              </h3>
              <ul className="text-sm text-blue-800 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Récupération automatique de vos formations</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Enrichissement avec les référentiels RNCP</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Analyse intelligente des compétences fusionnables</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Identification des opportunités d'optimisation</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Étape 2: Processing */}
        {step === 'processing' && (
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold mb-4">Analyse en cours...</h2>
              <p className="text-gray-600 mb-8">{progressMessage}</p>

              {organism && (
                <div className="p-4 bg-green-50 rounded-lg mb-6">
                  <p className="text-sm text-green-800">
                    <strong>Organisme :</strong> {organism.raisonSociale}
                  </p>
                  {formations.length > 0 && (
                    <p className="text-sm text-green-700 mt-1">
                      {formations.length} formation{formations.length > 1 ? 's' : ''} détectée{formations.length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}

              {enrichedFormations.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>{enrichedFormations.length}</strong> formation{enrichedFormations.length > 1 ? 's' : ''} enrichie{enrichedFormations.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    <strong>{enrichedFormations.reduce((sum, f) => sum + f.competences.length, 0)}</strong> compétences analysées
                  </p>
                </div>
              )}

              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                <span>Cela peut prendre quelques minutes...</span>
              </div>
            </div>
          </div>
        )}

        {/* Étape 3: Results */}
        {step === 'results' && comparisonData && (
          <div className="space-y-6">
            {/* Résumé principal */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <Award className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold mb-2">🎉 Résultats de l'analyse</h2>
                <p className="text-gray-600">
                  Nous avons identifié de belles opportunités d'optimisation !
                </p>
              </div>

              {/* Métriques principales */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <div className="text-4xl font-bold text-blue-600 mb-2">
                    {comparisonData.fusedCount}
                  </div>
                  <div className="text-sm text-gray-700">
                    Compétences fusionnées identifiées
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                  <div className="text-4xl font-bold text-purple-600 mb-2">
                    {comparisonData.unificationRate}%
                  </div>
                  <div className="text-sm text-gray-700">
                    Taux d'unification possible
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-6 h-6 text-green-600" />
                    <div className="text-4xl font-bold text-green-600">
                      {comparisonData.timeSaved}h
                    </div>
                  </div>
                  <div className="text-sm text-gray-700">
                    Temps gagné estimé
                  </div>
                </div>
              </div>

              {/* Message de recommandation */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6 mb-8">
                <h3 className="text-xl font-bold mb-3">
                  💡 Opportunité détectée
                </h3>
                <p className="text-lg mb-2">
                  En fusionnant <strong>{comparisonData.formation1.title}</strong> et <strong>{comparisonData.formation2.title}</strong>,
                  vous pourriez unifier <strong>{comparisonData.unificationRate}%</strong> de vos référentiels de compétences.
                </p>
                <p className="text-sm opacity-90">
                  Gain de temps estimé : <strong>{comparisonData.timeSaved} heures</strong> ({comparisonData.fusedCount} compétences × 4h de travail pédagogique)
                </p>
              </div>

              {/* CTA Principal */}
              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-8 border-2 border-green-300">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Prêt à optimiser vos formations ?
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Découvrez nos offres et commencez à économiser du temps dès aujourd'hui
                  </p>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
                  >
                    <Sparkles className="w-6 h-6" />
                    Voir les offres
                    <ArrowRight className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>

            {/* Comparaison détaillée */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold">
                  📊 Comparaison détaillée
                </h3>

                {/* Toggle pour afficher uniquement les compétences fusionnées */}
                <button
                  onClick={() => setShowOnlyFused(!showOnlyFused)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    showOnlyFused
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {showOnlyFused ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  <span className="text-sm font-medium">
                    {showOnlyFused ? 'Compétences fusionnées uniquement' : 'Toutes les compétences'}
                  </span>
                </button>
              </div>

              {/* Grille côte-à-côte */}
              <div className="grid grid-cols-2 gap-6">
                {/* Formation 1 (gauche) */}
                <div className="border-2 border-blue-300 rounded-lg">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg">
                    <h4 className="font-bold text-lg">{comparisonData.formation1.title}</h4>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-3 py-1 bg-white bg-opacity-20 rounded text-sm">
                        {comparisonData.formation1.rncpCode}
                      </span>
                      <span className="text-sm opacity-90">
                        {comparisonData.formation1.competences.length} compétences
                      </span>
                    </div>
                  </div>

                  {/* Blocs de compétences */}
                  <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
                    {comparisonData.formation1.blocks && comparisonData.formation1.blocks.map((bloc, bIndex) => {
                      const fusedInBloc = comparisonData.sharedFusions.filter(fusion => {
                        const source1 = fusion.sources.find(s => s.rncp === comparisonData.formation1.rncpCode);
                        return source1 && bloc.competences.includes(source1.text);
                      });

                      if (showOnlyFused && fusedInBloc.length === 0) return null;

                      const competencesToShow = showOnlyFused
                        ? bloc.competences.filter(comp =>
                            comparisonData.sharedFusions.some(fusion =>
                              fusion.sources.some(s => s.rncp === comparisonData.formation1.rncpCode && s.text === comp)
                            )
                          )
                        : bloc.competences;

                      if (competencesToShow.length === 0) return null;

                      return (
                        <div key={bIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gradient-to-r from-blue-100 to-blue-200 px-3 py-2">
                            <h5 className="text-sm font-semibold text-blue-900">
                              {bloc.code} - {bloc.libelle}
                            </h5>
                            <p className="text-xs text-blue-700 mt-1">
                              {showOnlyFused ? competencesToShow.length : bloc.competences.length} compétence{(showOnlyFused ? competencesToShow.length : bloc.competences.length) > 1 ? 's' : ''}
                              {fusedInBloc.length > 0 && (
                                <span className="ml-2 px-2 py-0.5 bg-green-600 text-white rounded-full">
                                  {fusedInBloc.length} fusionnée{fusedInBloc.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="p-3 bg-white">
                            <ul className="space-y-2">
                              {competencesToShow.map((comp, cIndex) => {
                                const isFused = comparisonData.sharedFusions.some(fusion =>
                                  fusion.sources.some(s => s.rncp === comparisonData.formation1.rncpCode && s.text === comp)
                                );

                                return (
                                  <li key={cIndex} className={`flex items-start text-xs ${isFused ? 'bg-green-50 border-l-4 border-green-500 pl-2 py-1' : ''}`}>
                                    <span className={`inline-flex items-center justify-center w-5 h-5 mr-2 text-xs font-medium rounded-full flex-shrink-0 mt-0.5 ${isFused ? 'bg-green-600 text-white' : 'bg-blue-100 text-blue-600'}`}>
                                      {cIndex + 1}
                                    </span>
                                    <span className="text-gray-700 flex-1">
                                      {comp}
                                      {isFused && (
                                        <span className="ml-2 text-green-600 font-semibold">✓</span>
                                      )}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Formation 2 (droite) */}
                <div className="border-2 border-purple-300 rounded-lg">
                  <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 rounded-t-lg">
                    <h4 className="font-bold text-lg">{comparisonData.formation2.title}</h4>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-3 py-1 bg-white bg-opacity-20 rounded text-sm">
                        {comparisonData.formation2.rncpCode}
                      </span>
                      <span className="text-sm opacity-90">
                        {comparisonData.formation2.competences.length} compétences
                      </span>
                    </div>
                  </div>

                  {/* Blocs de compétences */}
                  <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
                    {comparisonData.formation2.blocks && comparisonData.formation2.blocks.map((bloc, bIndex) => {
                      const fusedInBloc = comparisonData.sharedFusions.filter(fusion => {
                        const source2 = fusion.sources.find(s => s.rncp === comparisonData.formation2.rncpCode);
                        return source2 && bloc.competences.includes(source2.text);
                      });

                      if (showOnlyFused && fusedInBloc.length === 0) return null;

                      const competencesToShow = showOnlyFused
                        ? bloc.competences.filter(comp =>
                            comparisonData.sharedFusions.some(fusion =>
                              fusion.sources.some(s => s.rncp === comparisonData.formation2.rncpCode && s.text === comp)
                            )
                          )
                        : bloc.competences;

                      if (competencesToShow.length === 0) return null;

                      return (
                        <div key={bIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gradient-to-r from-purple-100 to-purple-200 px-3 py-2">
                            <h5 className="text-sm font-semibold text-purple-900">
                              {bloc.code} - {bloc.libelle}
                            </h5>
                            <p className="text-xs text-purple-700 mt-1">
                              {showOnlyFused ? competencesToShow.length : bloc.competences.length} compétence{(showOnlyFused ? competencesToShow.length : bloc.competences.length) > 1 ? 's' : ''}
                              {fusedInBloc.length > 0 && (
                                <span className="ml-2 px-2 py-0.5 bg-green-600 text-white rounded-full">
                                  {fusedInBloc.length} fusionnée{fusedInBloc.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="p-3 bg-white">
                            <ul className="space-y-2">
                              {competencesToShow.map((comp, cIndex) => {
                                const isFused = comparisonData.sharedFusions.some(fusion =>
                                  fusion.sources.some(s => s.rncp === comparisonData.formation2.rncpCode && s.text === comp)
                                );

                                return (
                                  <li key={cIndex} className={`flex items-start text-xs ${isFused ? 'bg-green-50 border-l-4 border-green-500 pl-2 py-1' : ''}`}>
                                    <span className={`inline-flex items-center justify-center w-5 h-5 mr-2 text-xs font-medium rounded-full flex-shrink-0 mt-0.5 ${isFused ? 'bg-green-600 text-white' : 'bg-purple-100 text-purple-600'}`}>
                                      {cIndex + 1}
                                    </span>
                                    <span className="text-gray-700 flex-1">
                                      {comp}
                                      {isFused && (
                                        <span className="ml-2 text-green-600 font-semibold">✓</span>
                                      )}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Légende */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Légende :</h4>
                <div className="flex gap-4 text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-50 border-l-4 border-green-500"></div>
                    <span>Compétence fusionnée (commune aux 2 formations)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-white border border-gray-200"></div>
                    <span>Compétence unique à cette formation</span>
                  </div>
                </div>
              </div>

              {/* CTA Secondaire */}
              <div className="mt-8 text-center">
                <button
                  onClick={() => navigate('/pricing')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                >
                  <ArrowRight className="w-5 h-5" />
                  Passer à l'action - Voir les offres
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding2;
